// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/growth-score/pages/SearchPage.tsx (ADAPT)
// ADAPTED: reads our view model; the three actions are the ones for the lowest BFF block scores (`SEARCH_ACTIONS`). The "Draft the brief" buttons
// toasted "Not wired in the prototype yet"; here they are disabled with the planning hint (H3). The `@[640px]` container
// query is a normal breakpoint (`sm:`, P4-2).
/**
 * Growth Score → Search & AI, an assessment of its own: can shoppers, Google
 * and AI assistants find the brand, and can an AI agent buy from it. Each
 * block ranks against the same cohort; their mean is the Search & AI score.
 */
import { Badge } from '@/components/ui/shadcn/badge'
import { Button } from '@/components/ui/shadcn/button'
import { Card } from '@/components/ui/shadcn/card'
import { ASSESSMENT_COPY } from '../assessment-copy'
import { SEARCH_ACTIONS } from '../search-content'
import { GrowthPage, GrowthSection } from '../GrowthPage'
import { useGrowthScore } from '../GrowthScoreContext'
import { SearchBlocks, useSearchBlocks } from '../components/SearchBlocks'
import { DimensionIcon, ScoreBar, ScoreFigure } from '../components/visuals'

/** "Start with these three". */
const MAX_ACTIONS = 3

export function SearchPage() {
  const vm = useGrowthScore()
  const blocks = useSearchBlocks()
  // No score yet is shown as none: never a 0 in its place.
  const overall = vm.dimensions.find((d) => d.key === 'search')?.score ?? null
  // The actions of the three lowest-scoring blocks, lowest first (a tie keeps the BFF's order).
  const weakest = [...blocks].sort((a, b) => a.score - b.score).slice(0, MAX_ACTIONS).map((b) => SEARCH_ACTIONS[b.key])
  const scores: [string, number][] = blocks.map((b): [string, number] => [b.name, b.score])

  return (
    <GrowthPage
      title="Search & AI"
      back
      hero={{
        icon: <DimensionIcon dimension="search" />,
        subtitle: 'Can shoppers, Google and AI assistants find you, and can an AI agent buy from you?',
        aside:
          overall != null ? (
            <div className="flex w-36 flex-col items-end gap-1.5">
              <ScoreFigure value={overall} size="lg" />
              <ScoreBar value={overall} />
            </div>
          ) : undefined,
        children: (
          <div className="flex flex-wrap gap-x-8 gap-y-3 border-t border-ds-hairline pt-3">
            {scores.map(([name, value]) => (
              <div key={name} className="flex min-w-28 flex-col gap-1.5">
                <span className="text-label text-ds-text-2">{name}</span>
                <ScoreFigure value={value} />
                <ScoreBar value={value} toned />
              </div>
            ))}
          </div>
        ),
      }}
    >

      <SearchBlocks />

      <GrowthSection title="Start with these three" sub="From your lowest-scoring blocks, lowest first.">
        <div className="grid gap-4 sm:grid-cols-3">
          {weakest.map(([area, title, body]) => (
            <Card key={title} className="flex flex-col items-start gap-2 p-5">
              <Badge intent="neutral">{area}</Badge>
              <h3 className="text-heading-sm text-foreground">{title}</h3>
              <p className="flex-1 text-body-sm text-ds-text-2">{body}</p>
              <Button type="button" size="xs" disabled>
                Draft the brief
              </Button>
              <span className="text-label text-ds-text-3">{ASSESSMENT_COPY.planningAfterSignup}</span>
            </Card>
          ))}
        </div>
      </GrowthSection>
    </GrowthPage>
  )
}
